import React from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const PrivacyPolicy = () => {
  useDocumentTitle('Privacy Policy');
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="bg-white rounded-lg shadow-md p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Privacy Policy</h1>
        <p className="text-gray-500 mb-6">Last Updated: 03/26/2026</p>
        
        <div className="prose prose-lg max-w-none text-gray-700">
          <p className="mb-4">
            This Privacy Policy explains how <span className="font-semibold">MyBrickLog.com</span> ("we," "us," or "our")
            collects, uses, and shares information when you use our website and related services (the "Site").
            By using the Site, you agree to the practices described in this Privacy Policy.
          </p>
          
          <p className="mb-6">
            Please note that <span className="font-semibold">MyBrickLog.com</span> is a fan site and is not affiliated with, endorsed by, or sponsored by LEGO Group 
            or its affiliates. LEGO and its associated trademarks, logos, and other intellectual property are owned by LEGO Group 
            and its licensors. The use of LEGO assets and branding on this site is intended for fan purposes and complies with 
            intellectual property laws.
          </p>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">1. Information We Collect</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><span className="font-semibold">Account data:</span> Username, email address, password hash, profile details, and account preferences.</li>
            <li><span className="font-semibold">Collection activity:</span> Data you create on the Site, such as sets added to your collection or wishlist, comments, and related actions.</li>
            <li><span className="font-semibold">Technical and usage data:</span> IP address, browser type, device information, pages visited, timestamps, referrer URL, and similar diagnostics.</li>
            <li><span className="font-semibold">Operational and security logs:</span> We keep basic server/application logs such as login attempts and key account actions (for example, "username attempted login" or "user added set to collection").</li>
            <li><span className="font-semibold">Cookies and similar technologies:</span> We use essential cookies for core functionality and may use optional cookies/identifiers for analytics or advertising where allowed by law and your consent choices.</li>
          </ul>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">2. How We Use Your Information</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>To provide and maintain our services.</li>
            <li>To manage your account, authentication, and account security.</li>
            <li>To record and monitor operational events, troubleshoot issues, detect abuse, and protect the Site.</li>
            <li>To improve and personalize your experience on the Site.</li>
            <li>To communicate with you, including transactional messages and optional newsletters you request.</li>
            <li>To measure usage and, where applicable, display ads via Google AdSense.</li>
          </ul>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">3. Legal Bases for Processing (Where Applicable)</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><span className="font-semibold">Contract:</span> Processing needed to provide account features you request.</li>
            <li><span className="font-semibold">Legitimate interests:</span> Security, abuse prevention, service reliability, and product improvement.</li>
            <li><span className="font-semibold">Consent:</span> Optional cookies and certain advertising/measurement activities (where consent is required).</li>
            <li><span className="font-semibold">Legal obligations:</span> Compliance with law and valid legal process.</li>
          </ul>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">4. Information Sharing and Disclosure</h2>
          <p className="mb-2">
            We do not sell your personal information for money. We may share information in the following situations:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><span className="font-semibold">Service providers:</span> Hosting, infrastructure, email, analytics, and similar vendors processing data on our behalf.</li>
            <li><span className="font-semibold">Advertising partner (Google AdSense):</span> AdSense may collect or receive data (including cookie/device identifier data and usage data) to serve and measure ads. Google may use this data for personalized or non-personalized advertising in accordance with its policies.</li>
            <li><span className="font-semibold">Legal requirements:</span> When required by law or to protect rights, safety, and security.</li>
            <li><span className="font-semibold">Business transfers:</span> In connection with a merger, acquisition, financing, or asset sale.</li>
          </ul>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">5. Google AdSense and Advertising</h2>
          <p className="mb-2">
            We may display ads using Google AdSense. Google and its partners may use cookies and similar technologies to serve ads based on your visit to this and other websites.
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>You can learn more about how Google uses data at <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">Google's partner sites policy</a>.</li>
            <li>You can control ad personalization in <a href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>.</li>
            <li>Where required by law, we request consent before enabling non-essential advertising technologies.</li>
          </ul>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">6. Data Retention</h2>
          <p className="mb-6">
            We retain personal information only as long as reasonably necessary for the purposes described in this Policy, including providing the Site, maintaining security logs, meeting legal obligations, resolving disputes, and enforcing agreements.
          </p>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">7. Data Security</h2>
          <p className="mb-6">
            We implement a variety of security measures to maintain the safety of your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
          </p>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">8. Your Rights and Choices</h2>
          <p className="mb-2">
            Depending on your jurisdiction, you may have the following rights regarding your personal information:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><span className="font-semibold">Access and Update:</span> You can access and update your personal information through your account settings.</li>
            <li><span className="font-semibold">Deletion:</span> You may request the deletion of your personal information by contacting us at <span className="font-semibold">legal@mybricklog.com</span>.</li>
            <li><span className="font-semibold">Withdrawal of Consent:</span> You can withdraw your consent to our data processing at any time by deleting your account or contacting us.</li>
            <li><span className="font-semibold">Cookie choices:</span> You can change cookie preferences using the "Cookie settings" control in the Site footer and through your browser settings.</li>
          </ul>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">9. Cookies and Tracking Technologies</h2>
          <p className="mb-6">
            We use essential cookies and local storage for core functions such as authentication, security, and user preferences. We may also use optional cookies/identifiers for analytics and advertising (including Google AdSense), subject to applicable consent requirements.
          </p>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">10. International Data Transfers</h2>
          <p className="mb-6">
            Depending on your location, your information may be processed in countries other than your own. Where required, we use appropriate safeguards for international transfers.
          </p>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">11. Children's Privacy</h2>
          <p className="mb-6">
            The Site is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided personal information, contact us and we will take appropriate steps.
          </p>
          
          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">12. Changes to This Privacy Policy</h2>
          <p className="mb-6">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on the Site. You are advised to review this Privacy Policy periodically for any changes.
          </p>

          <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">13. Contact Us</h2>
          <p className="mb-6">
            For privacy questions or requests, contact us at <span className="font-semibold">legal@mybricklog.com</span>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;